import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Trash2, ChevronRight, ImageOff } from "lucide-react";
import { getHistory, removeFromHistory, clearHistory, mergeServerHistory } from "../lib/history";
import { getMyMoodcards } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import ThemeToggle from "../components/ThemeToggle";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const absUrl = (u) => (u && u.startsWith("/") ? `${BACKEND_URL}${u}` : u);

const STYLE_ACCENT = {
    relatable: "#9FE7D9",
    poetic: "#F2B49B",
    bold: "#E5E5EA",
};

const formatDate = (iso) => {
    try {
        const d = new Date(iso);
        const now = new Date();
        const diffMs = now - d;
        const min = Math.floor(diffMs / 60000);
        if (min < 1) return "just now";
        if (min < 60) return `${min}m ago`;
        const hr = Math.floor(min / 60);
        if (hr < 24) return `${hr}h ago`;
        const day = Math.floor(hr / 24);
        if (day < 7) return `${day}d ago`;
        return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } catch {
        return "";
    }
};

export default function History() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [syncing, setSyncing] = useState(false);

    useEffect(() => { setItems(getHistory()); }, []);

    useEffect(() => {
        if (!user) return;
        setSyncing(true);
        getMyMoodcards(50)
            .then((server) => setItems(mergeServerHistory(server)))
            .catch(() => {})
            .finally(() => setSyncing(false));
    }, [user]);

    const handleRemove = (e, id) => {
        e.stopPropagation();
        removeFromHistory(id);
        setItems(getHistory());
    };

    const handleClear = () => {
        if (!window.confirm("Clear all saved moods on this device?")) return;
        clearHistory();
        setItems([]);
    };

    return (
        <div className="relative min-h-screen w-full bg-app overflow-x-hidden" data-testid="history-screen">
            <div className="absolute inset-0 aurora-wash opacity-30 pointer-events-none" />
            <div className="absolute inset-0 grain pointer-events-none" />

            <header className="relative z-10 max-w-md mx-auto px-6 pt-6 flex items-center justify-between">
                <button
                    onClick={() => navigate("/")}
                    className="flex items-center gap-2 text-app-2 hover:text-app transition-colors"
                    data-testid="history-back-button"
                >
                    <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
                    <span className="font-sans text-xs uppercase tracking-[0.3em]">home</span>
                </button>
                <div className="flex items-center gap-2">
                    <ThemeToggle />
                    {items.length > 0 && (
                        <button
                            onClick={handleClear}
                            data-testid="history-clear-button"
                            className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-app/30 text-app-2 hover:text-app hover:bg-surface-2 transition-colors"
                        >
                            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                            <span className="font-sans text-[10px] uppercase tracking-[0.3em]">clear</span>
                        </button>
                    )}
                </div>
            </header>

            <div className="relative z-10 max-w-md mx-auto px-6 pt-10 pb-24">
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                    <p className="font-sans text-[10px] uppercase tracking-[0.4em] text-app-3 mb-3">
                        {user ? "on every device" : "on this device"}{syncing && <span className="ml-2 normal-case tracking-normal text-app-3">syncing…</span>}
                    </p>
                    <h2 className="font-serif font-light text-app text-3xl sm:text-4xl leading-tight tracking-tight">
                        your past <span className="italic text-app-2">feelings.</span>
                    </h2>
                    <p className="mt-3 font-sans text-sm text-app-3 leading-relaxed max-w-[34ch]">
                        Saved quietly in your browser. Never sent to a feed, never shown to anyone but you.
                    </p>
                </motion.div>

                {items.length === 0 ? (
                    <div className="mt-16 text-center" data-testid="history-empty">
                        <div className="mx-auto h-14 w-14 rounded-full border border-app/30 bg-surface-1 flex items-center justify-center mb-5">
                            <ImageOff className="w-5 h-5 text-app-3" strokeWidth={1.4} />
                        </div>
                        <p className="font-serif italic text-app-2 text-lg">nothing here yet.</p>
                        <p className="mt-2 font-sans text-sm text-app-3">Your first MoodCard will appear here.</p>
                        <button
                            onClick={() => navigate("/")}
                            data-testid="history-make-first"
                            className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-cta text-app-inverse font-sans text-sm font-medium tracking-wide hover:opacity-90 transition-opacity"
                            style={{ backgroundColor: "var(--app-text)" }}
                        >
                            Make my first MoodCard
                        </button>
                    </div>
                ) : (
                    <ul className="mt-10 mc-history-stack" data-testid="history-list">
                        {items.map((m, i) => {
                            const accent = STYLE_ACCENT[m.selected_style] || "#8A857E";
                            // Alternating tilt for tactile fanned-stack feel. Cap rotation small.
                            const tilt = (i % 2 === 0 ? 0.55 : -0.55) - (i === 0 ? 0.55 : 0);
                            return (
                                <motion.li
                                    key={m.id}
                                    initial={{ opacity: 0, y: 14 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: Math.min(i * 0.04, 0.6), ease: [0.16, 1, 0.3, 1] }}
                                    className="mc-history-item"
                                    style={{ "--mc-tilt": `${tilt}deg`, zIndex: items.length - i }}
                                >
                                    <div
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => navigate(`/results/${m.id}`)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" || e.key === " ") {
                                                e.preventDefault();
                                                navigate(`/results/${m.id}`);
                                            }
                                        }}
                                        data-testid={`history-item-${m.id}`}
                                        className="mc-history-card group focus:outline-none"
                                    >
                                        <div className="relative w-16 h-24 shrink-0 rounded-xl overflow-hidden border border-app/30 bg-app-2">
                                            {m.preview_bg ? (
                                                <>
                                                    <img
                                                        src={absUrl(m.preview_bg)}
                                                        alt=""
                                                        className="absolute inset-0 w-full h-full object-cover"
                                                        draggable={false}
                                                    />
                                                    <div className="absolute inset-0 bg-black/40" />
                                                </>
                                            ) : null}
                                            <div
                                                className="absolute bottom-1 left-1 right-1 h-[2px] rounded-full"
                                                style={{ backgroundColor: accent, opacity: 0.6 }}
                                            />
                                        </div>
                                        <div className="flex-1 min-w-0 flex flex-col justify-between py-1">
                                            <p className="font-serif italic text-app text-base leading-snug line-clamp-2">
                                                "{m.feeling}"
                                            </p>
                                            <div className="flex items-center justify-between mt-2">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-app-3">
                                                        {formatDate(m.created_at)}
                                                    </span>
                                                    {m.selected_style && (
                                                        <span
                                                            className="font-sans text-[10px] uppercase tracking-[0.3em]"
                                                            style={{ color: accent }}
                                                        >
                                                            · {m.selected_style}
                                                        </span>
                                                    )}
                                                </div>
                                                <span className="flex items-center gap-1 text-app-3 group-hover:text-app transition-colors">
                                                    <ChevronRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        onClick={(e) => handleRemove(e, m.id)}
                                        data-testid={`history-remove-${m.id}`}
                                        className="mc-history-remove"
                                        aria-label="Remove"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                                    </button>
                                </motion.li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
}
